import { useEffect } from 'react';
import { Spinner } from '../../components/Spinner';
import type { FunnelStage, FunnelView } from './PredictionFunnel';

export interface Prediction {
  id: number;
  statement: string;
  influencer_name: string;
  prediction_date: string;
  direction: string;
  topic: string;
  asset: string | null;
  confidence_pct: number;
  timeframe_raw: string | null;
  brier_score: number | null;
  outcome: string | null;
}

export interface FunnelDrilldownPanelProps {
  stage: FunnelStage;
  view: FunnelView;
  rows: Prediction[];
  total: number;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onViewChange: (view: FunnelView) => void;
}

const directionColors: Record<string, string> = {
  bullish: 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/30',
  bearish: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30',
  neutral: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800',
  warning: 'text-yellow-700 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/30',
  other: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-800',
};

const outcomeColors: Record<string, string> = {
  correct: 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/30',
  incorrect: 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30',
  partial: 'text-yellow-700 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/30',
  unknown: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800',
};

function PredictionRow({ p }: { p: Prediction }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4 shadow-sm">
      <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap">
        {p.statement}
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">{p.influencer_name}</span>
        <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{p.prediction_date}</span>
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            directionColors[p.direction] || directionColors.other
          }`}
        >
          {p.direction}
        </span>
        <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
          {p.topic}
          {p.asset ? `: ${p.asset}` : ''}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{p.confidence_pct}% conf</span>
        {p.timeframe_raw && (
          <span className="text-xs text-gray-500 dark:text-gray-400">{p.timeframe_raw}</span>
        )}
        {p.brier_score != null && (
          <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
            Brier {p.brier_score.toFixed(2)}
          </span>
        )}
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            outcomeColors[p.outcome ?? 'unknown'] || outcomeColors.unknown
          }`}
        >
          {p.outcome}
        </span>
      </div>
    </div>
  );
}

export default function FunnelDrilldownPanel({
  stage,
  view,
  rows,
  total,
  loading,
  error,
  onClose,
  onViewChange,
}: FunnelDrilldownPanelProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const viewLabel = view === 'survivors' ? 'survived' : 'dropped';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 dark:bg-black/60 p-4 pt-12 sm:pt-20"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`${stage.label} — ${viewLabel}`}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {stage.label}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {view === 'survivors'
                ? `${stage.count.toLocaleString()} rows passed this stage`
                : `${stage.drop.toLocaleString()} rows lost here`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex gap-2">
          <button
            type="button"
            onClick={() => onViewChange('survivors')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              view === 'survivors'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Survivors
          </button>
          {stage.drop > 0 && (
            <button
              type="button"
              onClick={() => onViewChange('dropped')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                view === 'dropped'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              Dropped
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="h-48 flex flex-col items-center justify-center gap-3">
              <Spinner size={32} />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading rows…</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No rows for this view.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {rows.length.toLocaleString()} of {total.toLocaleString()} rows
              </p>
              {rows.map((p) => (
                <PredictionRow key={p.id} p={p} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
